import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import styles from "./CallSetting.module.css";
import { API_BASE_URL, token, updateAgentFromBackend } from "../../Store/apiStore";

// Retell's ambient-sound presets. `value` is whatccc Retell expects; "none" clears it.
const AMBIENT_OPTIONS = [
  { label: "Coffee Shop", value: "coffee-shop" },
  { label: "Convention Hall", value: "convention-hall" },
  { label: "Summer Outdoor", value: "summer-outdoor" },
  { label: "Mountain Outdoor", value: "mountain-outdoor" },
  { label: "Static Noise", value: "static-noise" },
  { label: "Call Center", value: "call-center" },
  { label: "None", value: "none" },
];

export default function BackgroundSound({ agentId }) {
  // Fall back to the id the rest of Call Setting uses.
  const resolvedAgentId = agentId || sessionStorage.getItem("SelectAgentId");
  const [value, setValue] = useState("call-center");
  const [volume, setVolume] = useState(1);
  const [saving, setSaving] = useState(false);
  const [showVolume, setShowVolume] = useState(false);
  const [volHover, setVolHover] = useState(false);
  const popoverRef = useRef(null);
  const gearRef = useRef(null);

  useEffect(() => {
    if (!resolvedAgentId) return;
    (async () => {
      try {
        const res = await axios.get(
          `${API_BASE_URL}/agent/getAgent/${resolvedAgentId}`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );
        const ambient = res.data?.ambient_sound;
        setValue(ambient ? ambient : "none");
        const vol = res.data?.ambient_sound_volume;
        if (vol != null) setVolume(vol);
      } catch (error) {
        console.error("Error fetching background sound:", error);
      }
    })();
  }, [resolvedAgentId]);

  // Close the volume popover on outside click.
  useEffect(() => {
    if (!showVolume) return;
    const onDocClick = (e) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(e.target) &&
        gearRef.current &&
        !gearRef.current.contains(e.target)
      ) {
        setShowVolume(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [showVolume]);

  const handleChange = async (e) => {
    const next = e.target.value;
    const prev = value;
    setValue(next); // optimistic
    if (!resolvedAgentId) return;
    try {
      setSaving(true);
      await updateAgentFromBackend({
        agent_id: resolvedAgentId,
        ambient_sound: next,
      });
    } catch (error) {
      console.error("Failed to update background sound:", error);
      setValue(prev); // revert on failure
    } finally {
      setSaving(false);
    }
  };

  // DEBOUNCED (~400ms after the last change) and the slider is NEVER disabled —
  // disabling mid-drag is what made it feel "stuck" on rapid moves.
  const volumeCommitTimer = useRef(null);
  const commitVolume = (vol) => {
    if (!resolvedAgentId) return;
    clearTimeout(volumeCommitTimer.current);
    volumeCommitTimer.current = setTimeout(async () => {
      try {
        setSaving(true);
        await updateAgentFromBackend({
          agent_id: resolvedAgentId,
          ambient_sound_volume: vol,
        });
      } catch (error) {
        console.error("Failed to update background sound volume:", error);
      } finally {
        setSaving(false);
      }
    }, 400);
  };

  return (
    <div>
      <p className={styles.description}>
        Play a subtle ambient sound in the background of the call.
      </p>
      <div className={styles.bgSoundRow}>
        <div className={styles.selectWrapper}>
          <select
            className={styles.bgSoundSelect}
            value={value}
            onChange={handleChange}
            disabled={saving}
          >
            {AMBIENT_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        {/* Gear → Background Sound Volume popover */}
        <div className={styles.gearWrap}>
          <button
            type="button"
            ref={gearRef}
            className={styles.gearBtn}
            onClick={() => setShowVolume((s) => !s)}
            aria-label="Background sound settings"
            disabled={value === "none"}
            title={
              value === "none"
                ? "Select a background sound first"
                : "Background sound volume"
            }
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#6b7280"
              strokeWidth="1.7"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="12" r="3" />
              <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
            </svg>
          </button>

          {showVolume && (
            <div className={styles.volumePopover} ref={popoverRef}>
              <p className={styles.volumeTitle}>Background Sound Volume</p>
              <div
                onPointerEnter={() => setVolHover(true)}
                onPointerLeave={() => setVolHover(false)}
              >
                <div className={styles.sliderWrapper}>
                  <input
                    type="range"
                    min="0"
                    max="2"
                    step="0.1"
                    value={volume}
                    onChange={(e) => setVolume(parseFloat(e.target.value))}
                    onMouseUp={(e) => commitVolume(parseFloat(e.target.value))}
                    onTouchEnd={(e) => commitVolume(parseFloat(e.target.value))}
                    className={styles.sliderRange}
                    style={{
                      background: `linear-gradient(to right, #693bff ${
                        (volume / 2) * 100
                      }%, #fff ${(volume / 2) * 100}%)`,
                      cursor: "pointer",
                    }}
                  />
                  <span className={styles.sliderValue}>{volume.toFixed(1)}</span>
                </div>
                <div
                  className={`${styles.minMaxRow} ${
                    volHover ? styles.minMaxRowVisible : ""
                  }`}
                >
                  <span>Low</span>
                  <span>High</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
